'use client';
import React, { useState, useEffect, useCallback } from 'react';
import PostCard from '@/app/components/PostCard';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';

export interface Post {
  id: string;
  userId: string;
  content: string;
  slug: string | null;
  agreeCount: number;
  disagreeCount: number;
  boost: boolean;
  doubleVote: boolean;
  createdAt: string;
  boostExpiresAt: string | null;
  userVote: 'agree' | 'disagree' | null;
  supportCount: number;
}

function mapRow(row: any, userId: string | null, votes: any[]): Post {
  const postVote = userId ? votes.find((v) => v.post_id === row.id && v.user_id === userId) : null;

  return {
    id: row.id,
    userId: row.user_id,
    content: row.content,
    slug: row.slug ?? null,
    agreeCount: row.agree_count,
    disagreeCount: row.disagree_count,
    boost: row.boost,
    doubleVote: row.double_vote,
    createdAt: row.created_at,
    boostExpiresAt: row.boost_expires_at ?? null,
    userVote: postVote ? postVote.vote_type : null,
    supportCount: row.support_count ?? 0,
  };
}

export default function PostFeed() {
  const { user } = useAuth();
  const router = useRouter();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const supabase = createClient();

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select('*')
        .order('boost', { ascending: false })
        .order('created_at', { ascending: false });

      if (postsError) throw postsError;

      const postIds = postsData?.map((p) => p.id) ?? [];

      const votesRes = postIds.length > 0
        ? await supabase.from('votes').select('*').in('post_id', postIds)
        : { data: [], error: null };

      const mapped = (postsData ?? []).map((row) =>
        mapRow(row, user?.id ?? null, votesRes.data ?? [])
      );

      setPosts(mapped);
    } catch (err: any) {
      setError(err.message ?? 'Failed to load posts.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // After login, auto-apply pending vote
  useEffect(() => {
    if (!user) return;
    const pending = localStorage.getItem('pending_vote');
    if (!pending) return;
    localStorage.removeItem('pending_vote');
    try {
      const { postId, type } = JSON.parse(pending) as { postId: string; type: 'agree' | 'disagree' };
      if (postId && type) {
        applyVote(postId, type);
      }
    } catch {
      // ignore malformed data
    }
  }, [user]);

  const applyVote = async (postId: string, type: 'agree' | 'disagree') => {
    if (!user) return;
    // Optimistic update
    setPosts((prev) =>
      prev.map((p) => {
        if (p.id !== postId) return p;
        const multiplier = p.doubleVote ? 2 : 1;
        const wasVoted = p.userVote === type;
        const prevVote = p.userVote;
        return {
          ...p,
          userVote: wasVoted ? null : type,
          agreeCount:
            type === 'agree'
              ? wasVoted
                ? p.agreeCount - multiplier
                : p.agreeCount + multiplier
              : prevVote === 'agree'
              ? p.agreeCount - multiplier
              : p.agreeCount,
          disagreeCount:
            type === 'disagree'
              ? wasVoted
                ? p.disagreeCount - multiplier
                : p.disagreeCount + multiplier
              : prevVote === 'disagree'
              ? p.disagreeCount - multiplier
              : p.disagreeCount,
        };
      })
    );

    try {
      const { error } = await supabase.rpc('cast_vote', {
        p_user_id: user.id,
        p_post_id: postId,
        p_vote_type: type,
      });
      if (error) {
        fetchPosts();
      }
    } catch {
      fetchPosts();
    }
  };

  const handleVote = async (postId: string, type: 'agree' | 'disagree') => {
    if (!user) {
      // Save pending vote and redirect to login
      localStorage.setItem('pending_vote', JSON.stringify({ postId, type }));
      router.push('/sign-up-login-screen');
      return;
    }
    await applyVote(postId, type);
  };

  const handleSupportUpdate = (postId: string, newSupportCount: number, newAgreeCount: number) => {
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, supportCount: newSupportCount, agreeCount: newAgreeCount }
          : p
      )
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-red-500">{error}</p>
        <button
          onClick={fetchPosts}
          className="mt-3 text-xs text-primary underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-muted-foreground">No posts yet. Be the first to say something.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {posts.map((post) => (
        <PostCard
          key={post.id}
          post={post}
          onVote={handleVote}
          onSupportUpdate={handleSupportUpdate}
        />
      ))}
    </div>
  );
}